import React, { useState, useEffect } from 'react';
import { useIsMobile } from '../../hooks/useIsMobile';

let activeModalScrollLocks = 0;

export default function Modal({
  show,
  onCancel,
  onConfirm,
  title,
  content,
  loading = false,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmBtnClass = 'btn-primary',
  cancelBtnClass = 'btn-secondary',
  confirmDisabled = false,
  footer,
  headerExtra = null, // New prop for custom header content
  size = 'modal-dialog-centered', // Can be 'modal-lg', 'modal-sm', etc.
  className = '',
}) {
  const isMobile = useIsMobile();
  const [internalLoading, setInternalLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  
  // Handle modal visibility with animation
  useEffect(() => {
    if (show) {
      setShouldRender(true);
      setIsClosing(false);
    } else if (shouldRender) {
      setIsClosing(true);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
      }, 300); // Match animation duration
      return () => clearTimeout(timer);
    }
  }, [show, shouldRender]);
  
  // Reset internal loading when modal is closed
  useEffect(() => {
    if (!show) {
      setInternalLoading(false);
    }
  }, [show]);

  // Lock background page scroll while any modal is open.
  useEffect(() => {
    if (!show || typeof document === 'undefined') {
      return undefined;
    }

    const body = document.body;
    const html = document.documentElement;

    if (activeModalScrollLocks === 0) {
      body.dataset.modalPrevOverflow = body.style.overflow || '';
      html.dataset.modalPrevOverflow = html.style.overflow || '';
    }

    activeModalScrollLocks += 1;
    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';

    return () => {
      activeModalScrollLocks = Math.max(0, activeModalScrollLocks - 1);
      if (activeModalScrollLocks === 0) {
        body.style.overflow = body.dataset.modalPrevOverflow || '';
        html.style.overflow = html.dataset.modalPrevOverflow || '';
        delete body.dataset.modalPrevOverflow;
        delete html.dataset.modalPrevOverflow;
      }
    };
  }, [show]);
  
  // Combined loading state - either external prop or internal state
  const isLoading = loading || internalLoading;

  const modalContentClassNames = [
    'modal-content',
    isClosing ? 'modal-content-closing' : 'modal-content-custom',
    className
  ].filter(Boolean).join(' ');
  
  // Enhanced onConfirm handler that triggers loading
  const handleConfirm = async () => {
    if (onConfirm) {
      setInternalLoading(true);
      try {
        await onConfirm();
      } catch (error) {
        // If onConfirm throws, still reset loading
        setInternalLoading(false);
        throw error;
      }
      setInternalLoading(false);
    }
  };
  
  // Render immediately when show is true.
  // Previously, the modal relied on an effect to set shouldRender=true,
  // which could prevent the modal from ever painting during long-running click handlers.
  if (!shouldRender && !show) return null;

  return (
    <>
      <style>
        {`
          @keyframes modalFadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          @keyframes modalFadeOut {
            from {
              opacity: 1;
            }
            to {
              opacity: 0;
            }
          }

          @keyframes modalSlideDown {
            from {
              opacity: 0;
              transform: translateY(-50px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes modalSlideUp {
            from {
              opacity: 1;
              transform: translateY(0);
            }
            to {
              opacity: 0;
              transform: translateY(-50px);
            }
          }

          .modal-backdrop-custom {
            animation: modalFadeIn 0.3s ease-out;
          }

          .modal-backdrop-closing {
            animation: modalFadeOut 0.3s ease-in;
          }

          .modal-content-custom {
            animation: modalSlideDown 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
          }

          .modal-content-closing {
            animation: modalSlideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
          }
        `}
      </style>
      <div 
        className={`modal fade show ${isClosing ? 'modal-backdrop-closing' : 'modal-backdrop-custom'}`} 
        tabIndex="-1" 
        style={{ 
          display: 'block', 
          background: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(4px)',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflowY: 'hidden', // Prevent scrolling the entire modal
          zIndex: 1050 // Higher than bottom nav's 1000
        }}
      >
        <div className={`modal-dialog ${size}`} style={{
          maxHeight: isMobile ? 'calc(100vh - 120px)' : '90vh',
          margin: '0',
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1051
        }}>
          <div 
            className={modalContentClassNames}
            style={{
              borderRadius: '12px',
              border: 'none',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              background: '#fff',
              maxWidth: isMobile ? '98vw' : '700px',
              width: '100%',
              maxHeight: isMobile ? 'calc(100vh - 120px)' : '90vh',
              height: 'auto', // Let content determine height
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <div className="modal-header" style={{ borderBottom: '1px solid #f1f5f9', padding: '1.5rem', overflow: 'visible' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                  <h5 className="modal-title" style={{ fontWeight: '600', color: '#1a202c', margin: 0 }}>
                    <span style={{ fontSize: 'clamp(1.25rem, 2vw + 0.75rem, 1.6rem)', fontWeight: 700 }}>{title}</span>
                  </h5>
                  {headerExtra && (
                    <div style={{ marginLeft: 'auto', marginRight: '1rem' }}>
                      {headerExtra}
                    </div>
                  )}
                </div>
                {onCancel && (
                  <button 
                    type="button" 
                    onClick={onCancel}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '2rem',
                      fontWeight: 700,
                      color: '#888',
                      cursor: 'pointer',
                      lineHeight: 1,
                      flexShrink: 0
                    }}
                    aria-label="Close"
                  >
                    &times;
                  </button>
                )}
              </div>
            </div>
            <div className="modal-body" style={{ 
              padding: '1.5rem',
              overflowX: 'hidden', // Fix horizontal overflow
              overflowY: 'auto',   // Allow vertical scrolling if needed
              flex: '1 1 auto',    // Allow growth and shrinking as needed
              minHeight: 0,        // Allow shrinking below content size
              maxHeight: isMobile ? 'calc(100vh - 300px)' : 'calc(90vh - 250px)' // Conservative to ensure footer fits
            }}>
              {content}
            </div>
            <div className="modal-footer" style={{ 
              borderTop: '1px solid #f1f5f9', 
              padding: '1.5rem', 
              gap: '0.75rem',
              flexShrink: 0, // Never shrink the footer
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center'
            }}>
              {footer ? (
                footer
              ) : (
                <>
                  {cancelText && onCancel && (
                    <button 
                      className={`btn ${cancelBtnClass}`} 
                      onClick={onCancel}
                      disabled={isLoading}
                      style={{
                        fontWeight: '500',
                        padding: '0.75rem 1.5rem',
                        borderRadius: '8px',
                        fontSize: '0.875rem'
                      }}
                    >
                      {cancelText}
                    </button>
                  )}
                  <button 
                    className={`btn ${confirmBtnClass}`} 
                    onClick={handleConfirm} 
                    disabled={isLoading || confirmDisabled}
                    style={{
                      backgroundColor: confirmBtnClass === 'btn-danger' ? '#ef4444' : undefined,
                      border: 'none',
                      fontWeight: '500',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      opacity: (isLoading || confirmDisabled) ? 0.6 : 1
                    }}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm" style={{ marginRight: '0.5rem' }} />
                        {typeof isLoading === 'string' ? isLoading : 'Loading...'}
                      </>
                    ) : (
                      confirmText
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}